/**
 * An attribute, and where it came from.
 *
 * Every value the platform shows carries a provenance chip, so a revenue figure
 * a business typed and a revenue figure drawn from e-invoicing records are
 * never visually interchangeable. The confidence on the chip is fixed per
 * verification method rather than tuned per attribute: authoritative API 1.000,
 * cross-source agreement 0.950, document review 0.900, self declared 0.600. A
 * tuned confidence would be a number somebody could argue with on a case the
 * platform had an interest in.
 *
 * Withheld keys are listed, not hidden. A provider seeing a pack of eight
 * attributes should know that four more exist and consent does not cover them,
 * because a short pack that looks complete invites a decline on missing data.
 */
import type { Dict } from '../api/types'
import { useSession } from '../app/session'
import { Badge } from './bits'

export interface Provenance {
  source?: string
  method?: string
  confidence?: number
  fresh?: boolean
  observed_at?: string
}

const METHOD_CLASS: Record<string, string> = {
  authoritative_api: 'auth',
  document_review: 'doc',
  cross_source_agreement: 'cross',
  model_derived: 'model',
  self_declared: 'decl',
}

export function ProvenanceChip({ p }: { p?: Provenance | null }) {
  const { n } = useSession()
  const method = p?.method || 'self_declared'
  const cls = METHOD_CLASS[method] || 'decl'
  const fresh = p ? p.fresh !== false : false
  const confidence = Number(p?.confidence ?? 0)
  return (
    <span className={`prov ${cls} ${fresh ? '' : 'stale'}`}
          title={`source ${p?.source || 'unknown'} · method ${method} · `
                 + `confidence ${confidence.toFixed(3)} · ${fresh ? 'current' : 'stale'}`}>
      <span className="pip" />
      {(p?.source || 'unknown').replace(/_/g, ' ')}
      <b>{n(confidence, 2)}</b>
      {!fresh && ' stale'}
    </span>
  )
}

/**
 * The attribute pack as a table.
 *
 * `definitions` gives each key its human name and unit where the handler sent
 * them; without one the key is shown as it stands rather than prettified, since
 * a key is what somebody will search the attribute register for.
 */
export function AttributeTable({ attrs, provenance, definitions, stale, withheld }: {
  attrs: Record<string, unknown>
  provenance?: Record<string, Provenance>
  definitions?: Record<string, Dict>
  stale?: string[]
  withheld?: string[]
}) {
  const { lbl, dt } = useSession()
  const keys = Object.keys(attrs || {}).sort()
  const staleSet = new Set(stale || [])

  return (
    <>
      <div className="tbl-wrap">
        <table className="dl">
          <thead>
            <tr>
              <th>Attribute</th><th>Value</th><th>Where it came from</th>
              <th>Observed</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => {
              const definition = definitions?.[key]
              const p = provenance?.[key]
              const value = attrs[key]
              return (
                <tr key={key}>
                  <td data-label="Attribute">
                    <div>{(definition?.name_en as string) || lbl(key)}</div>
                    <div className="xs muted"><code>{key}</code></div>
                  </td>
                  <td data-label="Value">
                    <b>{value === null || value === undefined ? '—' : String(value)}</b>
                    {definition?.unit ? ` ${definition.unit as string}` : ''}
                    {staleSet.has(key) && <> <Badge tone="warn">stale</Badge></>}
                  </td>
                  <td data-label="Where it came from"><ProvenanceChip p={p} /></td>
                  <td data-label="Observed">
                    {p?.observed_at ? dt(p.observed_at) : <span className="muted">—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {withheld && withheld.length > 0 && (
        <div className="cav" style={{ marginTop: 12 }}>
          <b>{withheld.length} further attribute{withheld.length === 1 ? '' : 's'}{' '}
          exist{withheld.length === 1 ? 's' : ''} and are not disclosed here.</b>{' '}
          Consent does not cover {withheld.length === 1 ? 'it' : 'them'} for this
          purpose. The keys are named so that an assessment is not made on the
          assumption that nothing more exists:{' '}
          {withheld.map((k) => <code key={k} style={{ marginInlineEnd: 5 }}>{k}</code>)}
        </div>
      )}

      <p className="xs muted">
        Attributes are never updated in place. A new observation supersedes the
        last, the current value is the latest by when it was observed rather than
        by when it was loaded, and the whole chain stays readable, which is what
        lets a decision taken in one year be replayed in another.
      </p>
    </>
  )
}
